/**
 * MixCompare metric readers — extracted so they can be unit-tested and so
 * there is one author per number.
 *
 * D1 (Jun 2026): `co2IntensityGkWh` used to derive the intensity from
 * `energy_per_source_kwh` with a single 0.40 kg/kWh factor for ALL gas, so an
 * OCGT plant read 400 g/kWh in MixCompare while the carbon card read 550.
 * Order of authority is now:
 *   1. `model.co2_intensity_g_kwh` — written by the engine (rebind + fold +
 *      dispatch propagation). Same field the carbon card reads.
 *   2. the scored candidate's scoreboard row.
 *   3. legacy derivation, for results stored before (1) existed — per-tech
 *      factors, supplied by the backend via GET /api/config. No emission
 *      factor is authored in the frontend.
 */

export const co2IntensityGkWh = (r, factors = null) => {
  const m = r?.model || {};

  // 1) Engine-published intensity — one source of truth with the carbon card.
  const published = Number(m.co2_intensity_g_kwh);
  if (Number.isFinite(published) && published > 0) return published;

  // 2) Scored candidate row.
  const chosen = (r?.candidates?.selections?.[r?.inputs_echo?.priority || "lowest_lifetime"]?.kind)
                  || r?.candidates?.chosen_kind;
  if (chosen && Array.isArray(r?.candidates?.scoreboard)) {
    const row = r.candidates.scoreboard.find((c) => c.kind === chosen);
    if (row && row.co2_intensity_g_kwh != null) return Number(row.co2_intensity_g_kwh);
  }

  // 3) Legacy derivation. Needs the backend's factor map; without it we say
  //    nothing rather than invent a factor.
  const annual = Number(m.annual_energy_kwh || 0);
  const ef = factors?.factors;
  if (!annual || !ef) return null;
  const src = m.energy_per_source_kwh || {};
  const gasTech = m.thermal_tech || "gas";
  const gridEf = gridEfFor(r, factors);
  const kg = Number(src.diesel || 0) * Number(ef.diesel || 0)
             + Number(src.hfo || 0) * Number(ef.hfo || 0)
             + Number(src.grid || 0) * gridEf
             + Number(src.gas || 0) * Number(ef[gasTech] ?? ef.gas ?? 0);
  return Math.round((kg / annual) * 1000 * 10) / 10;
};

const gridEfFor = (r, factors) => {
  const country = r?.inputs_echo?.country || r?.model?.country;
  const byCountry = factors?.grid || {};
  const fallback = Number(factors?.gridDefault || 0);
  const v = country != null ? byCountry[country] : undefined;
  return v == null ? fallback : Number(v);
};

/** Normalise the GET /api/config payload into the shape the readers want. */
export const emissionFactorsFromConfig = (cfg) => {
  if (!cfg?.emission_factors_kg_per_kwh) return null;
  return {
    factors: cfg.emission_factors_kg_per_kwh,
    grid: cfg.grid_emission_factors_kg_per_kwh || {},
    gridDefault: cfg.grid_emission_factor_default_kg_per_kwh,
  };
};
